import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const STATUS_LABELS = {
  free: 'Libre',
  occupied: 'Ocupada',
  reserved: 'Reservada',
  cleaning: 'Limpieza',
};

const STATUS_COLORS = {
  free: '#10b981',
  occupied: '#ef4444',
  reserved: '#f59e0b',
  cleaning: '#38bdf8',
};

const TablesPage = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [form, setForm] = useState({ number: '', name: '', capacity: 4 });

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tables');
      setTables(res.data.data || []);
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg ? `Error del servidor: ${msg}` : 'No se pudieron cargar las mesas.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ number: '', name: '', capacity: 4 });
    setEditingTable(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await api.put(`/tables/${editingTable.id}`, form);
      } else {
        await api.post('/tables', form);
      }
      setShowForm(false);
      resetForm();
      fetchTables();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar la mesa');
    }
  };

  const startEdit = (table) => {
    setEditingTable(table);
    setForm({ number: table.number, name: table.name || '', capacity: table.capacity || 4 });
    setShowForm(true);
  };

  const changeStatus = async (table, status) => {
    try {
      await api.put(`/tables/${table.id}`, { status });
      fetchTables();
    } catch (err) {
      alert('Error al actualizar el estado');
    }
  };

  const deleteTable = async (table) => {
    if (!window.confirm(`¿Quitar la mesa "${table.number}"?`)) return;
    try {
      const res = await api.delete(`/tables/${table.id}`);
      if (res.data.message) {
        // Puede haberse desactivado en vez de eliminado (si tiene historial de pedidos)
        alert(res.data.message);
      }
      fetchTables();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar la mesa');
    }
  };

  if (loading) return <p style={{ color: '#94a3b8', textAlign: 'center', padding: 50 }}>Cargando mesas...</p>;

  if (error) return (
    <div>
      <h1 style={{ color: '#e2e8f0', fontSize: 28, marginBottom: 24 }}>🪑 Mesas</h1>
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>
        <button onClick={fetchTables} className="btn btn-primary">Reintentar</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: '#e2e8f0', fontSize: 28 }}>🪑 Mesas</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="btn btn-primary">
          {showForm ? 'Cancelar' : '+ Agregar Mesa'}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 500 }}>
          <h3 style={{ color: '#e2e8f0', marginBottom: 16 }}>
            {editingTable ? '✏️ Editar Mesa' : 'Nueva Mesa'}
          </h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Número *</label>
              <input type="text" placeholder="Ej: 11" className="input" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} required />
            </div>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Nombre (opcional)</label>
              <input type="text" placeholder="Ej: Terraza 3, VIP C" className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Capacidad</label>
              <input type="number" min="1" className="input" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-success">
              {editingTable ? '💾 Guardar Cambios' : '+ Crear Mesa'}
            </button>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {tables.map(table => (
          <div key={table.id} className="card" style={{ borderLeft: `4px solid ${STATUS_COLORS[table.status] || '#334155'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ color: '#e2e8f0', fontSize: 20 }}>Mesa {table.number}</h3>
              <span style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: (STATUS_COLORS[table.status] || '#334155') + '20',
                color: STATUS_COLORS[table.status] || '#94a3b8',
              }}>
                {STATUS_LABELS[table.status] || table.status}
              </span>
            </div>
            {table.name && <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 4 }}>{table.name}</p>}
            <p style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>Capacidad: {table.capacity} personas</p>

            <select
              className="input"
              value={table.status}
              onChange={e => changeStatus(table, e.target.value)}
              style={{ marginBottom: 10, fontSize: 12, padding: '6px 10px' }}
            >
              {Object.entries(STATUS_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>

            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => startEdit(table)} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✏️ Editar</button>
              <button onClick={() => deleteTable(table)} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑️ Quitar</button>
            </div>
          </div>
        ))}
      </div>

      {tables.length === 0 && (
        <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No hay mesas registradas todavía.</p>
      )}
    </div>
  );
};

export default TablesPage;